import React, { useEffect, useState } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, Empty, StatusBadge } from "@/components/common";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { Loader2, FileText, Receipt, Search, X, FileSpreadsheet, CheckCircle2, Clock } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function AdminTransactions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(null);
  const [exp, setExp] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const filterParams = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    return params;
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/transactions", { params: filterParams() }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [dateFrom, dateTo, status, q]);

  const clearFilters = () => { setDateFrom(""); setDateTo(""); setStatus("all"); setQ(""); };

  const downloadInvoice = async (sessionId) => {
    setDl(sessionId);
    try {
      const r = await api.get(`/payments/invoice/${sessionId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${sessionId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh invoice"); }
    finally { setDl(null); }
  };

  const exportRekap = async (format) => {
    setExp(format);
    try {
      const r = await api.get("/admin/transactions/export", { params: { ...filterParams(), format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `transaksi_online_${today}.${format === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh rekap"); }
    finally { setExp(null); }
  };

  const hasFilter = dateFrom || dateTo || status !== "all" || q.trim();

  const totalNominal = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const paidRows = rows.filter((r) => r.status === "paid");
  const paidNominal = paidRows.reduce((s, r) => s + (r.amount || 0), 0);
  const pendingRows = rows.filter((r) => r.status === "pending");
  const pendingNominal = pendingRows.reduce((s, r) => s + (r.amount || 0), 0);
  const cards = [
    { label: "Total Transaksi", value: rows.length + " transaksi", sub: rupiah(totalNominal), testid: "tx-card-total", cls: "text-stone-800", icon: <Receipt size={18} className="text-stone-400" /> },
    { label: "Lunas", value: paidRows.length + " transaksi", sub: rupiah(paidNominal), testid: "tx-card-paid", cls: "text-emerald-700", icon: <CheckCircle2 size={18} className="text-emerald-500" /> },
    { label: "Menunggu", value: pendingRows.length + " transaksi", sub: rupiah(pendingNominal), testid: "tx-card-pending", cls: "text-amber-700", icon: <Clock size={18} className="text-amber-500" /> },
  ];

  return (
    <div>
      <PageHeader title="Transaksi Online" subtitle="Riwayat pembayaran via iPaymu. Unduh invoice/bukti transaksi bila diperlukan (mis. verifikasi iPaymu)." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wide text-stone-400">{c.label}</span>
              {c.icon}
            </div>
            <p className={`text-xl font-bold ${c.cls}`}>{c.value}</p>
            <p className="text-sm font-mono text-stone-500 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Dari Tanggal</label>
          <DateField value={dateFrom} onChange={setDateFrom} testid="tx-date-from" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Sampai Tanggal</label>
          <DateField value={dateTo} onChange={setDateTo} testid="tx-date-to" />
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-xl bg-white h-12" data-testid="tx-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="paid">Lunas</SelectItem>
              <SelectItem value="pending">Menunggu</SelectItem>
              <SelectItem value="expired">Kadaluarsa</SelectItem>
              <SelectItem value="cancelled">Dibatalkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Cari Nama Santri / Wali</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="tx-search" placeholder="Ketik nama…" className="rounded-xl bg-white h-12 pl-9" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm text-stone-500" data-testid="tx-count">
          {rows.length} transaksi{hasFilter ? " ditemukan (sesuai filter)" : " (semua)"}
        </p>
        <div className="flex items-center gap-2">
          {hasFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="tx-clear-filters" className="text-stone-500 rounded-lg"><X size={14} className="mr-1" /> Reset filter</Button>
          )}
          <Button variant="outline" size="sm" onClick={() => exportRekap("pdf")} disabled={exp !== null || rows.length === 0} data-testid="tx-export-pdf" className="rounded-lg">
            {exp === "pdf" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />} Ekspor PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportRekap("xlsx")} disabled={exp !== null || rows.length === 0} data-testid="tx-export-excel" className="rounded-lg text-emerald-700 border-emerald-200">
            {exp === "xlsx" ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileSpreadsheet size={14} className="mr-1" />} Ekspor Excel
          </Button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : rows.length === 0 ? (
          <Empty><Receipt className="mx-auto mb-2" /> {hasFilter ? "Tidak ada transaksi sesuai filter." : "Belum ada transaksi online."}</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Tanggal</TableHead>
                <TableHead>Wali</TableHead>
                <TableHead>Rincian</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>No. Ref iPaymu</TableHead>
                <TableHead className="text-right">Invoice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.session_id} data-testid={`tx-row-${r.session_id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                  <TableCell className="font-medium">{r.parent_name}</TableCell>
                  <TableCell className="text-sm text-stone-500">{r.summary}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold">{rupiah(r.amount)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="font-mono text-xs text-stone-500">{r.trx_id || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadInvoice(r.session_id)} disabled={dl === r.session_id} data-testid={`invoice-btn-${r.session_id}`} className="rounded-lg">
                      {dl === r.session_id ? <Loader2 size={14} className="animate-spin" /> : (<><FileText size={14} className="mr-1" /> Unduh</>)}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
